import { Link } from "react-router-dom";
import { Play, Pause, ShoppingBag, BookOpen, ExternalLink } from "lucide-react";
import { useRef, useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const { addItem } = useCart();
  const isMusic = product.type === "music";
  const isTemplate = product.type === "template";
  const isEbook = product.type === "ebook";
  const isMerch = product.type === "merch";
  const detailHref = isMerch ? "/merch" : `/product/${product.id}`;
  const fallbackImg = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><rect width='100%' height='100%' fill='%23141414'/><text x='50%' y='50%' fill='%23D4AF37' font-family='serif' font-size='42' text-anchor='middle' dominant-baseline='middle'>HOH</text></svg>";

  const togglePlay = (e) => {
    e.preventDefault();
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else audioRef.current.play().then(() => setPlaying(true)).catch(() => toast.error("Preview not available"));
  };

  const quickBuy = (e, format, price) => {
    e.preventDefault();
    if (!price) return;
    addItem(product, format, price, {});
    toast.success(`Added ${product.title} to basket`);
  };

  // Merch cards never use the cart — they open the external shop.
  if (isMerch) {
    const href = product.external_url || "https://www.hohvibesshop.com/";
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={`product-card-${product.id}`}
        className="luxury-card group block overflow-hidden rounded-xl"
      >
        <div className="relative aspect-square overflow-hidden bg-black">
          <img src={product.cover_image_url || fallbackImg} alt={product.title} onError={(e) => { e.currentTarget.src = fallbackImg; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-md border border-white/10 rounded text-[10px] tracking-widest uppercase text-[#D4AF37]">
            {product.category}
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <h3 className="font-serif-display text-xl text-white group-hover:text-[#D4AF37] transition-colors leading-tight">{product.title}</h3>
            <p className="text-sm text-neutral-400 mt-1 line-clamp-2">{product.description}</p>
          </div>
          <div className="flex items-center justify-between pt-2">
            <div className="font-serif-display text-2xl text-[#D4AF37]">£{Number(product.price).toFixed(2)}</div>
            <span data-testid={`shop-${product.id}`} className="btn-gold py-2 px-4 rounded inline-flex items-center gap-2 text-xs tracking-widest uppercase">
              <ShoppingBag size={14} /> Shop <ExternalLink size={12} />
            </span>
          </div>
        </div>
      </a>
    );
  }

  return (
    <Link to={detailHref} data-testid={`product-card-${product.id}`} className="luxury-card group block overflow-hidden rounded-xl">
      <div className="relative aspect-square overflow-hidden bg-black">
        <img src={product.cover_image_url || fallbackImg} alt={product.title} onError={(e) => { e.currentTarget.src = fallbackImg; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-md border border-white/10 rounded text-[10px] tracking-widest uppercase text-[#D4AF37]">
          {product.category}
        </div>
        {isMusic && product.preview_audio_url && (
          <button
            data-testid={`preview-btn-${product.id}`}
            onClick={togglePlay}
            className="absolute bottom-3 right-3 w-12 h-12 rounded-full bg-[#D4AF37] text-black flex items-center justify-center hover:bg-[#FCE68A] transition-colors"
            aria-label="Preview"
          >
            {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
          </button>
        )}
        {isMusic && (<audio ref={audioRef} src={product.preview_audio_url} onEnded={() => setPlaying(false)} preload="none" />)}
      </div>
      <div className="p-5 space-y-3">
        <div>
          <h3 className="font-serif-display text-xl text-white group-hover:text-[#D4AF37] transition-colors leading-tight">{product.title}</h3>
          <p className="text-sm text-neutral-400 mt-1 line-clamp-2">{product.description}</p>
        </div>
        {isMusic ? (
          <div className="flex gap-2 pt-2">
            <button data-testid={`buy-mp3-${product.id}`} onClick={(e) => quickBuy(e, "mp3", product.price_mp3)} className="btn-ghost-gold flex-1 text-xs tracking-widest uppercase py-2 px-3 rounded">
              MP3 · £{Number(product.price_mp3).toFixed(2)}
            </button>
            <button data-testid={`buy-wav-${product.id}`} onClick={(e) => quickBuy(e, "wav", product.price_wav)} className="btn-gold flex-1 text-xs tracking-widest uppercase py-2 px-3 rounded">
              WAV · £{Number(product.price_wav).toFixed(2)}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between pt-2">
            <div>
              <div className="text-[10px] tracking-widest text-neutral-500 uppercase">
                {isTemplate && product.file_type}
                {isEbook && (product.file_type || "PDF")}
              </div>
              <div className="font-serif-display text-2xl text-[#D4AF37]">£{Number(product.price).toFixed(2)}</div>
            </div>
            <button
              data-testid={`buy-${product.type}-${product.id}`}
              onClick={(e) => quickBuy(e, isEbook ? "ebook" : "template", product.price)}
              className="btn-gold py-2 px-4 rounded inline-flex items-center gap-2 text-xs tracking-widest uppercase">
              {isEbook ? <BookOpen size={14} /> : <ShoppingBag size={14} />}
              Buy
            </button>
          </div>
        )}
      </div>
    </Link>
  );
}
